import { useCallback, useEffect, useRef } from 'react';

export interface FormDraftImage {
  name: string;
  type: string;
  dataUrl: string;
}

export interface FormDraftPayload {
  savedAt: number;
  fields: Record<string, unknown>;
  tags: { tag: string }[];
  tagsEN: { tag: string }[];
  image: FormDraftImage | null;
  imageEN: FormDraftImage | null;
}

const DEBOUNCE_MS = 500;

export const fileToDraftImage = (file: File): Promise<FormDraftImage> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        name: file.name,
        type: file.type,
        dataUrl: reader.result as string,
      });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export const draftImageToFile = (image: FormDraftImage): File => {
  const base64 = image.dataUrl.split(',')[1] ?? '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], image.name, { type: image.type });
};

const useFormDraft = (draftKey: string) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveDraft = useCallback(
    (payload: Omit<FormDraftPayload, 'savedAt'>) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        try {
          const fullPayload: FormDraftPayload = {
            ...payload,
            savedAt: Date.now(),
          };
          localStorage.setItem(draftKey, JSON.stringify(fullPayload));
        } catch (error) {
          // Quota exceeded or storage unavailable — losing autosave is
          // acceptable, crashing the form is not.
          console.error('No se pudo guardar el borrador:', error);
        }
      }, DEBOUNCE_MS);
    },
    [draftKey],
  );

  const loadDraft = useCallback((): FormDraftPayload | null => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return null;
      return JSON.parse(raw) as FormDraftPayload;
    } catch (error) {
      console.error('No se pudo leer el borrador guardado:', error);
      localStorage.removeItem(draftKey);
      return null;
    }
  }, [draftKey]);

  const clearDraft = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    localStorage.removeItem(draftKey);
  }, [draftKey]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  return { saveDraft, loadDraft, clearDraft };
};

export default useFormDraft;
